import { IS_FR, tr } from '@/lib/i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useHaptic } from '@/hooks/useHaptic';
import { useCoachUnread } from '@/hooks/useCoachUnread';
import { useEffect, useMemo, useRef } from 'react';
// 25/09: ícones próprios (Astra, image_gen), pedido dele: "icones para os botoes de
// navegacao". Dois arquivos por aba, o mesmo desenho: `-off` todo cinza e `-on` com
// o acento. Eram os de linha do lucide.
interface NavItem {
  icon: string;
  label: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = IS_FR ? [
  // 12/10: com 4 abas, "Les 3 phrases" quebrava em duas linhas a 375 px.
  { icon: 'home', label: tr('Home'), path: '/' },
  { icon: 'phrases', label: '3 phrases', path: '/magic-words' },
  { icon: 'sarah', label: tr('Sarah'), path: '/expert-chat' },
  { icon: 'profile', label: tr('Profile'), path: '/profile' },
] : [
  {
    icon: 'home',
    label: 'Home',
    path: '/',
  },
  {
    icon: 'phrases',
    // Chamava-se "Magic Words" e levava a /scripts, enquanto a tarja da home
    // chamada "magic words" leva a /magic-words. Dois nomes iguais, dois
    // destinos diferentes. A aba é a biblioteca de frases; o nome diz isso.
    label: 'Phrases',
    path: '/scripts',
  },
  {
    icon: 'sarah',
    label: 'Sarah',
    path: '/expert-chat',
  },
  {
    // 24/09: a aba é só de livros agora (as vídeo-aulas saíram). O endereço
    // continua /learn para não quebrar link salvo; o nome e o ícone dizem o que tem.
    icon: 'books',
    label: 'Books',
    path: '/learn',
  },
  {
    icon: 'profile',
    label: 'Profile',
    path: '/profile',
  },
];

// 22/09/2026 (grill do dashboard com o Codex Astra): cada aba tinha um degradê
// próprio (laranja, roxo, ciano, rosa, verde), brilho colorido e uma faísca
// animada; a Sarah tinha um fundo ciano permanente, que parecia "você está
// aqui" em qualquer tela. Agora: a aba atual é uma cápsula no acento (token),
// com ícone e rótulo no acento e traço mais forte; as outras são cinza e sem
// fundo. Uma ênfase só, e ela diz onde a pessoa está.
function NavButton({ item, isActive, badge }: {
  item: NavItem;
  isActive: boolean;
  badge?: number;
}) {
  const navigate = useNavigate();
  const { triggerHaptic } = useHaptic();

  return (
    <button
      type="button"
      onClick={() => {
        triggerHaptic('medium');
        navigate(item.path);
      }}
      className={cn(
        "relative flex flex-col items-center justify-center flex-1 py-1 min-w-0 min-h-[48px] rounded-2xl",
        "transition-colors duration-200 touch-manipulation select-none active:scale-95",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        // 24/09 (grill da home com o Astra): saiu o fundo laranja-claro atrás da aba
        // ativa. Era mais uma mancha de acento fixa na tela, disputando com o botão do
        // método; a aba ativa fica marcada pela cor e pelo peso do ícone e do nome.
      )}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className="relative">
        {/* 28px (o piso dos ícones próprios), contra os 20px do lucide: o desenho tem
            miolo e precisa de área para ler. A altura da barra não muda: o py caiu de
            8 para 4px. */}
        <img
          src={`/images/nav/${item.icon}-${isActive ? 'on' : 'off'}.webp`}
          alt=""
          width={28}
          height={28}
          className="h-7 w-7"
          draggable={false}
        />
        {/* O badge era `{badge && badge > 0 && ...}`: com badge === 0 o React
            desenhava o próprio 0 embaixo do ícone da Sarah (22/09). */}
        {(badge ?? 0) > 0 && !isActive && (
          <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background" />
        )}
      </span>
      <span
        className={cn(
          // 15px, o piso da persona (era 10px até 20/09).
          "text-[15px] mt-1 leading-tight",
          isActive ? "text-accent font-bold" : "text-muted-foreground font-semibold"
        )}
      >
        {item.label}
      </span>
    </button>
  );
}

export function BottomNavExpandable() {
  const location = useLocation();
  const coachUnread = useCoachUnread();

  // 25/09: a barra publica a própria altura (com a safe-area) em --bottom-nav-h.
  // O campo da Sarah lia 96px fixo, medido em 21/09; a barra encolheu para 72,75px
  // e abriu um vão de ~23px por onde a conversa vazava (print dele). Número fixo em
  // dois arquivos já errou três vezes; agora quem se apoia na barra lê a medida dela.
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--bottom-nav-h', `${el.getBoundingClientRect().height}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--bottom-nav-h');
    };
  }, []);

  // O `-on` de cada aba só é pedido quando ela vira a atual; sem isto, o primeiro
  // toque em cada aba mostra o ícone vazio por um instante.
  useEffect(() => {
    NAV_ITEMS.forEach((item) => {
      ['on', 'off'].forEach((state) => {
        const img = new Image();
        img.src = `/images/nav/${item.icon}-${state}.webp`;
      });
    });
  }, []);

  const activeIndex = useMemo(() => {
    if (location.pathname === '/') return 0;
    const idx = NAV_ITEMS.findIndex(item =>
      item.path !== '/' && location.pathname.startsWith(item.path)
    );
    return idx >= 0 ? idx : null;
  }, [location.pathname]);

  return (
    // Nasce na posição final (22/09): com `initial={{ y: 100 }}`, se não chegasse
    // frame a barra ficava fora da tela.
    <nav
      ref={navRef}
      aria-label={tr("Main navigation")}
      className="fixed bottom-0 left-0 right-0 z-40 pointer-events-auto md:hidden bg-card"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)' }}
    >
      {/* Dissolve o conteudo antes da barra.
          Ele mostrou o print (21/09): ao rolar, o card e cortado numa linha reta
          pela borda da barra e o texto fica partido ao meio ("Brain food for
          universal"). A reserva do commit b466c307 resolve o FIM do scroll;
          isto resolve o MEIO, que e onde ela passa a maior parte do tempo.

          28px, e o numero nao e solto: a barra mede 96,8px + safe-area e as
          paginas com barra reservam 128px, entao sobram 31,2px de vazio no fim
          do scroll. O fade tem de caber nesse vazio, senao ele apaga o ultimo
          card justamente onde o outro conserto tinha acabado de revela-lo.
          28 < 31,2. Medido no fim do scroll: invasao de 0px.
          Quem mexer em um dos dois numeros mexe nos dois — ha teste.

          A alternativa rejeitada foi deixar a barra translucida de verdade (o
          padrao iOS): o fundo dela viraria imagem colorida em movimento e o
          contraste dos cinco rotulos deixaria de ser um numero fixo. Para a avo
          de 55 anos, rotulo legivel vence efeito.
          decoracao-ok: faixa de gradiente, sem controle dentro. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-full inset-x-0 h-7 bg-gradient-to-t from-background to-transparent"
      />

      <div className="absolute top-0 inset-x-0 h-[1px] bg-border/60" />

      {/* Nav items */}
      <div className="flex items-center px-2 pt-1.5 pb-1">
        {NAV_ITEMS.map((item, index) => (
          <NavButton
            key={item.path}
            item={item}
            isActive={activeIndex === index}
            badge={item.path === '/expert-chat' ? coachUnread : undefined}
          />
        ))}
      </div>
    </nav>
  );
}
